import * as Sentry from '@sentry/browser';
import { env } from '../config/env.js';
import { logger } from '../utils/logger.js';

/**
 * Initialize Sentry error monitoring
 */
export function initializeSentry() {
  if (!env.sentryDsn) {
    logger.warn('Sentry DSN not configured');
    return;
  }

  try {
    Sentry.init({
      dsn: env.sentryDsn,
      environment: env.isProduction ? 'production' : 'development',
      release: `bau-da-gata@${env.appVersion}`,
      
      // Performance monitoring
      tracesSampleRate: 0.1,
      
      // Session replay (only for production errors)
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: env.isProduction ? 0.5 : 0,

      // Filter out non-critical errors
      beforeSend(event, hint) {
        // Don't send errors in development
        if (!env.isProduction) {
          return null;
        }

        // Filter out network errors from third-party scripts
        const error = hint.originalException;
        if (error?.message?.includes('Script error')) {
          return null;
        }

        return event;
      },

      // Ignore certain URLs
      denyUrls: [
        /extensions\//i,
        /^chrome:\/\//i,
        /^moz-extension:\/\//i
      ]
    });

    logger.info('Sentry initialized');
  } catch (error) {
    logger.error('Failed to initialize Sentry:', error);
  }
}

/**
 * Capture exception in Sentry
 * @param {Error} error - Error to capture
 * @param {Record<string, any>} [context] - Additional context
 */
export function captureException(error, context = {}) {
  if (!env.isProduction) {
    logger.error('Error captured:', error, context);
    return;
  }

  Sentry.withScope((scope) => {
    Object.entries(context).forEach(([key, value]) => {
      scope.setExtra(key, value);
    });
    Sentry.captureException(error);
  });
}

/**
 * Capture message in Sentry
 * @param {string} message - Message to capture
 * @param {'info' | 'warning' | 'error'} [level] - Severity level
 */
export function captureMessage(message, level = 'info') {
  if (!env.isProduction) {
    logger[level]?.('Message captured:', message);
    return;
  }

  Sentry.captureMessage(message, level);
}

/**
 * Set user context for Sentry
 * @param {object} user - User information
 */
export function setUser(user) {
  Sentry.setUser(user);
}

/**
 * Add breadcrumb for debugging
 * @param {object} breadcrumb - Breadcrumb data
 */
export function addBreadcrumb(breadcrumb) {
  Sentry.addBreadcrumb({
    timestamp: Date.now() / 1000,
    ...breadcrumb
  });
}
